import * as THREE from 'three';
import { assert } from './ErrorHandling';
import { getUuidv4, JsonCopy } from './Misc';
import {
	FadeType,
	MaterialTypes,
	ObjectClasses,
	RenderModes,
	SaveModificationTypes,
	TimelineEffectorTypes
} from '../../globals';
import { ObjectTriggerTypes, VisibilityModes } from '../../interfaces/enums';
import AudioSource from '../AudioSource';
import Callout from '../Callout';
import ScopeCamera from '../Camera';
import Engine from '../Engine';
import { GroupsManager } from '../GroupsManager';
import ScopeLight from '../Light';
import MaterialAsset from '../MaterialAsset';
import MediaPanel from '../MediaPanel';
import ProjectSaver from '../ProjectSaver';
import Scenario from '../scenarioElements/Scenario';
import Text3D from '../Text3D';
import { Trigger } from '../../interfaces/Trigger';
import { BillboardSettings } from '../../interfaces/Billboard';
import EffectorObjectFade from '../timeline/effectors/EffectorObjectFade';
import TimelineObject from '../timeline/TimelineObject';
import ParentToNewEmptyAction from '../../actions/ParentToNewEmptyAction';
import VisibilityManager from '../VisibilityManager';

declare module 'three' {
	export interface Skeleton {
		guid?: string;
	}
	export interface AnimationClip {
		sceneGuid?: string;
	}
}

export class TwoKeySubObjectLookupDatabase
{
	private storage: Map<string, THREE.Object3D> = new Map<string, THREE.Object3D>();

	private generateCompositeKey(nodeIndex: number | undefined, primitiveIndex: number | undefined): string
	{
		return `${nodeIndex ?? 'undefined'}:${primitiveIndex ?? 'undefined'}`;
	}

	public set(nodeIndex: number | undefined, primitiveIndex: number | undefined, value: THREE.Object3D): void
	{
		const compositeKey = this.generateCompositeKey(nodeIndex, primitiveIndex);
		this.storage.set(compositeKey, value);
	}

	public get(nodeIndex: number | undefined, primitiveIndex: number | undefined): THREE.Object3D | undefined
	{
		const compositeKey = this.generateCompositeKey(nodeIndex, primitiveIndex);
		return this.storage.get(compositeKey);
	}

	public has(nodeIndex: number | undefined, primitiveIndex: number | undefined): boolean
	{
		const compositeKey = this.generateCompositeKey(nodeIndex, primitiveIndex);
		return this.storage.has(compositeKey);
	}

	public delete(nodeIndex: number | undefined, primitiveIndex: number | undefined): boolean
	{
		const compositeKey = this.generateCompositeKey(nodeIndex, primitiveIndex);
		return this.storage.delete(compositeKey);
	}
}

export default class SceneObjectUtils
{
	private static sceneCopyGuidMap: Map<string, string> = new Map();

	static getSceneCopyGuidMap(): Map<string, string>
	{
		return SceneObjectUtils.sceneCopyGuidMap;
	}

	/**
     * recursively searches the scene hierarchy for an object with a userData property that has the given name and value
     * @static
     * @param {THREE.Object3D} root
     * @param {String} name
     * @param {*} value
     * @returns {THREE.Object3D} the first found scene object, or undefined
     * @memberof SceneObjectUtils
     */
	static getObjectByUserDataProperty(root: THREE.Object3D, name: string, value: any): THREE.Object3D | undefined
	{
		if (root.userData[name] === value) return root;

		for (let i = 0, l = root.children.length; i < l; i++)
		{
			const child = root.children[i];
			const object = SceneObjectUtils.getObjectByUserDataProperty(child, name, value);

			if (object !== undefined)
			{
				return object;
			}
		}

		return undefined;
	}

	static initialiseObjectProperties(object: THREE.Object3D): void
	{
		object.traverse((child) =>
		{
			if (!child.userData.guid) child.userData.guid = getUuidv4();

			if (!child.userData.visibility)
			{
				child.userData.visibility = {
					overrideParent: false,
					standalone: VisibilityModes.Solid,
					device: VisibilityModes.Solid
				};
			}

			SceneObjectUtils.initialiseObjectMultipassRenderingCallback(child);
		});
	}

	static initialiseObjectMultipassRenderingCallback(object: THREE.Object3D): void
	{
		object.onAfterRender = function (renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, geometry: THREE.BufferGeometry, material: THREE.Material, group: any)
		{
			if ("requiresMultipassRendering" in material)
			{
				assert("multipassMaterials" in material, "Material with requiresMultipassRendering must have initialize mutlipassMaterials");
				if ("multipassMaterials" in material)
				{
					for (const multiPassMaterial of material.multipassMaterials as THREE.Material[])
					{
						if ("onMultipassRender" in multiPassMaterial)
						{
							(multiPassMaterial as any).onMultipassRender(camera, scene, geometry, renderer, this, group);
						}
						else
						{
							renderer.renderBufferDirect(camera, scene, geometry, multiPassMaterial, this, group);
						}
					}
				}
			}
		};
	}

	static reconstructSkeleton(skinnedMesh: THREE.SkinnedMesh, reassignBones = true): void
	{
		//ripped and modified from threejs SkeletonUtils.clone
		const assetRoot = Engine.getInstance().getModelAsset(skinnedMesh.userData.assetId);
		if (!assetRoot) return;
		const meshNodeIndex = skinnedMesh.userData.modelData.nodeIndex;
		const meshPrimitiveIndex = skinnedMesh.userData.modelData.primitiveIndex;
		const sourceMesh = SceneObjectUtils.getSubNode(assetRoot, meshNodeIndex, meshPrimitiveIndex, skinnedMesh.userData.assetId) as THREE.SkinnedMesh;
		const sourceBones = sourceMesh.skeleton.bones;

		skinnedMesh.skeleton = sourceMesh.skeleton.clone();
		skinnedMesh.skeleton.guid = sourceMesh.skeleton.guid;
		skinnedMesh.bindMatrix.copy(sourceMesh.bindMatrix);

		if (reassignBones)
		{
			let skeletonRoot: THREE.Object3D | null = skinnedMesh;
			//attempt to find the model root of this skinned mesh instance
			while (skeletonRoot)
			{
				if (skeletonRoot.userData.isModelRoot && skeletonRoot.userData.assetId === skinnedMesh.userData.assetId)
				{
					break;
				}
				skeletonRoot = skeletonRoot.parent;
			}

			if (skeletonRoot)
			{
				skinnedMesh.skeleton.bones = sourceBones.map((bone) =>
				{
					return SceneObjectUtils.getSubNode(skeletonRoot!, bone.userData.modelData.nodeIndex, bone.userData.modelData.primitiveIndex, skeletonRoot!.userData.assetId) as THREE.Bone;
				});
			}
		}

		skinnedMesh.bind(skinnedMesh.skeleton, skinnedMesh.bindMatrix);
	}

	static correctObjectAnimations(animationModelRoot: THREE.Object3D): void
	{
		assert(animationModelRoot.userData.animations);

		const object = animationModelRoot;
		const originalAnimations = Engine.getInstance().getModelAsset(object.userData.assetId)?.userData.animations as THREE.AnimationClip[];
		if (!originalAnimations) return;
		object.userData.animations = [];
		//go over all animationCLips and change the track names so that they point at the src object's uuids (since the exporter works against uuids)
		for (const anim of originalAnimations)
		{
			const animClone = anim.clone();
			animClone.uuid = anim.uuid; //the exporter looks up shared clip data through uuid. doing this to not bloat gltf file with duplicate data

			const numTracks = animClone.tracks.length;
			for (let i = numTracks - 1; i >= 0; i--)
			{
				const track = animClone.tracks[i];
				const trackBinding = THREE.PropertyBinding.parseTrackName(track.name);
				//find the object in modelRoot whose originalUuid is the same as the one the track is pointing
				const node = SceneObjectUtils.getObjectByUserDataProperty(object, 'originalUuid', trackBinding.nodeName);
				if (node)
				{
					track.name = node.uuid + '.' + trackBinding.propertyName;
				}
				else
				{
					animClone.tracks.splice(i, 1);
				}
			}
			if (animClone.tracks.length) object.userData.animations.push(animClone);
		}

		if (object.userData.animations.length)
		{
			Engine.getInstance().createAnimationMixer(object);
		}
		else
		{
			delete object.userData.animations;
		}
	}

	/**
     * traverses the children of objects a and b simulatanesously, invoking the given callback
     * it is expected that objects a and b have identical hierarchies
     * @static
     * @param {THREE.Object3D} a
     * @param {THREE.Object3D} b
     * @param {Function} callback
     * @memberof SceneObjectUtils
     */
	static parallelTraverse(a: THREE.Object3D, b: THREE.Object3D, callback: (a: THREE.Object3D, b: THREE.Object3D) => void): void
	{
		callback(a, b);

		if (!a || !b)
		{
			console.error("parallelTraverse: missing object in pair: ", a, b);
			return;
		}

		for (let i = 0; i < a.children.length; i++)
		{
			if (i > b.children.length - 1)
			{
				console.error("parallelTraverse: object a and b have different number of children even though they should have identical hierarchies! index: " + i, a, b);
				break;
			}

			SceneObjectUtils.parallelTraverse(a.children[i], b.children[i], callback);
		}
	}

	static groupObjectsUnderNewEmpty(objects: THREE.Object3D[])
	{
		if (objects.length === 0) return undefined;
		let parent: THREE.Object3D | undefined = objects[0]?.parent ?? undefined;
		parent = objects.every((object) => object.parent === parent) ? parent : undefined; 

		const objectsThatNeedMoving: Set<THREE.Object3D> = new Set();
		for (const object of objects)
		{
			if (!object.parent || !objects.includes(object.parent))
			{
				objectsThatNeedMoving.add(object);
				continue;
			}
			let parent = object.parent;
			while (parent && parent.parent && objects.includes(parent.parent))
			{
				parent = parent.parent;
			}
			if (parent) objectsThatNeedMoving.add(parent);
		}
		objects = objects.filter((object) => objectsThatNeedMoving.has(object));
		
		const emptyObject = new THREE.Object3D();
		emptyObject.name = 'Empty Object';
		SceneObjectUtils.initialiseObjectProperties(emptyObject);
		emptyObject.position.copy(SceneObjectUtils.calculateBoundsCenter(objects));

		new ParentToNewEmptyAction(objects, emptyObject, parent);
		
		return emptyObject;
	}

	static cloneObject(source: THREE.Object3D,
		isFullSceneCopy = false,
		isContinueSceneCopy = false,
		destSceneGuid: string | undefined = undefined,
		sourceStepGuid: string | undefined = undefined,
		cloneLookupTable: TwoKeySubObjectLookupDatabase | undefined = undefined): THREE.Object3D
	{
		const sourceToClone: Map<THREE.Object3D, THREE.Object3D> = new Map();
		const cloneToSource: Map<THREE.Object3D, THREE.Object3D> = new Map();
		const sourceGuidToSource: Map<string, THREE.Object3D> = new Map();

		const clonedSkinnedMeshes: THREE.SkinnedMesh[] = [];
		const clonedTriggersWithTargetObject: Trigger[] = [];
		const clonedBillboardsWithtargetObject: BillboardSettings[] = [];

		const clone = source.clone();
		if (isFullSceneCopy) SceneObjectUtils.sceneCopyGuidMap.clear();

		SceneObjectUtils.parallelTraverse(source, clone, function (sourceNode, clonedNode)
		{
			sourceToClone.set(sourceNode, clonedNode);
			cloneToSource.set(clonedNode, sourceNode);

			if (clonedNode.userData.guid)
			{
				clonedNode.userData.guid = getUuidv4();
				const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(sourceNode.userData.guid);
				if (materialAsset)
				{
					MaterialAsset.assignMaterialAssetToObject(materialAsset, clonedNode as THREE.Mesh);
					if (materialAsset.isHiddenFromView()) materialAsset.setHiddenInAssetView(false);
				}
				if (clonedNode.userData.animations)
				{
					SceneObjectUtils.correctObjectAnimations(clonedNode);
					if (clonedNode.userData.animations && destSceneGuid)
					{
						for (const anim of clonedNode.userData.animations as THREE.AnimationClip[])
						{
							anim.sceneGuid = destSceneGuid;
						}
						Engine.getInstance().registerObjectAnimationsForExport(clonedNode);
					}
				}
				sourceGuidToSource.set(sourceNode.userData.guid, sourceNode);
				if (isFullSceneCopy) SceneObjectUtils.sceneCopyGuidMap.set(sourceNode.userData.guid, clonedNode.userData.guid);
			}

			if (cloneLookupTable && clonedNode.userData.modelData)
			{
				cloneLookupTable.set(clonedNode.userData.modelData.nodeIndex, clonedNode.userData.modelData.primitiveIndex, clonedNode);
			}
			delete clonedNode.userData.fadeInFactor;
			delete clonedNode.userData.overrideColorFactor;
			delete clonedNode.userData.overrideColor;
			delete clonedNode.userData.animationStartColor;
			delete clonedNode.userData.defaultObjectGuid;
			delete clonedNode.userData.unlinkedDefaultObjectGuid;
			delete clonedNode.userData.premoveDefaultObjectGuid;
			delete clonedNode.userData.isCachedAssetObject;

			if (clonedNode instanceof THREE.SkinnedMesh)
			{
				clonedSkinnedMeshes.push(clonedNode);
			}
			if (clonedNode instanceof Callout)
			{
				Callout.setCallout(clonedNode.parent?.userData.guid, clonedNode);
				clonedNode.updateCanvas();
			}
			if (clonedNode instanceof ScopeCamera && destSceneGuid)
			{
				(clonedNode as ScopeCamera).onCameraAddedToScene(destSceneGuid);
			}
			if (clonedNode.userData.groups && destSceneGuid)
			{
				assert(destSceneGuid);
				for (const groupGuid of clonedNode.userData.groups)
				{
					GroupsManager.addToGroup(groupGuid, [clonedNode], destSceneGuid);
				}
			}
			if (clonedNode.userData.triggers)
			{
				if (isFullSceneCopy)
				{
					for (const trigger of clonedNode.userData.triggers as Trigger[])
					{
						if (trigger.targetObject)
						{
							clonedTriggersWithTargetObject.push(trigger);
						}
					}
				}
				else
				{
					for (let i = clonedNode.userData.triggers.length - 1; i >= 0; i--)
					{
						const trigger = clonedNode.userData.triggers[i] as Trigger;
						if (trigger.targetObject === sourceNode.userData.guid)
						{
							trigger.targetObject = clonedNode.userData.guid;
						}
						if (trigger.removedSyncedTrigger)
						{
							clonedNode.userData.triggers.splice(i, 1);
						}
						delete trigger.defaultSyncedTrigger;
					}
				}
			}
			if (clonedNode.userData.billboard && destSceneGuid)
			{
				if (clonedNode.userData.billboard.enabled)
				{
					Engine.getInstance().getBillboardManager().enableBillboard(clonedNode, destSceneGuid);
				}

				if (clonedNode.userData.billboard.targetGuid && isFullSceneCopy)
				{
					clonedBillboardsWithtargetObject.push(clonedNode.userData.billboard);
				}
			}

			if (sourceNode.onAfterRender) clonedNode.onAfterRender = sourceNode.onAfterRender;
		});

		for (const clonedSkinnedMesh of clonedSkinnedMeshes)
		{
			SceneObjectUtils.reconstructSkeleton(clonedSkinnedMesh, false);
			const sourceMesh = cloneToSource.get(clonedSkinnedMesh) as THREE.SkinnedMesh;
			const sourceBones = sourceMesh.skeleton.bones;
			clonedSkinnedMesh.skeleton.bones = sourceBones.map((bone) =>
			{
				const newBone = sourceToClone.get(bone) as THREE.Bone;
				if (newBone)
				{
					return newBone;
				}
				else
				{
					const assetRoot = Engine.getInstance().getModelAsset(clonedSkinnedMesh.userData.assetId);
					if (!assetRoot) return new THREE.Bone();
					const sourceAssetBone = SceneObjectUtils.getSubNode(assetRoot, bone.userData.modelData.nodeIndex, bone.userData.modelData.primitiveIndex, clonedSkinnedMesh.userData.assetId) as THREE.Bone;
					const dummyBone = new THREE.Bone();
					dummyBone.name = "DummyBone_" + sourceAssetBone.name;
					dummyBone.userData = JsonCopy(sourceAssetBone.userData);
					dummyBone.userData.hideInHierarchy = true;
					delete dummyBone.userData.guid;

					sourceAssetBone.matrixWorld.decompose(dummyBone.position, dummyBone.quaternion, dummyBone.scale);
					clonedSkinnedMesh.attach(dummyBone);
					return dummyBone;
				}
			});
		}

		for (const trigger of clonedTriggersWithTargetObject)
		{
			const sourceTargetObject = sourceGuidToSource.get(trigger.targetObject);
			if (sourceTargetObject)
			{
				const cloneTargetObject = sourceToClone.get(sourceTargetObject);
				if (cloneTargetObject)
				{
					trigger.targetObject = cloneTargetObject.userData.guid;
					ProjectSaver.getInstance().markModifiedObject(cloneTargetObject, SaveModificationTypes.Reference);
				}
			}
			else
			{
				trigger.targetObject = "";
			}
		}

		for (const billboard of clonedBillboardsWithtargetObject)
		{
			const sourceTargetObject = sourceGuidToSource.get(billboard.targetGuid!);
			if (sourceTargetObject)
			{
				const cloneTargetObject = sourceToClone.get(sourceTargetObject);
				if (cloneTargetObject)
				{
					billboard.targetGuid = cloneTargetObject.userData.guid;
					ProjectSaver.getInstance().markModifiedObject(cloneTargetObject, SaveModificationTypes.Reference);
				}
			}
			else
			{
				billboard.targetGuid = null;
			}
		}

		if (isContinueSceneCopy)
		{
			assert(sourceStepGuid);
			const sourceStep = Scenario.getStep(sourceStepGuid!);
			if (!sourceStep) return clone;
			sourceStep.timeline?.getChildren().forEach((group) =>
			{
				//there could be multiple timeline objects refering to the same scene objects (due to the possibility of hidden timeline objects)
				const guidToTimelineObjects: Map<string, TimelineObject[]> = new Map();
				group!.objects.forEach((timelineObject) =>
				{
					if (timelineObject.associatedObject && !timelineObject.associatedObject.userData.removedFromScene)
					{
						if (!guidToTimelineObjects.has(timelineObject.associatedObject.userData.guid))
						{
							guidToTimelineObjects.set(timelineObject.associatedObject.userData.guid, []);
						}
						guidToTimelineObjects.get(timelineObject.associatedObject.userData.guid)!.push(timelineObject);
					}
					else if (timelineObject.objectGroupGuid)
					{
						if (!guidToTimelineObjects.has(timelineObject.objectGroupGuid))
						{
							guidToTimelineObjects.set(timelineObject.objectGroupGuid, []);
						}
						guidToTimelineObjects.get(timelineObject.objectGroupGuid)!.push(timelineObject);
					}
				});
				guidToTimelineObjects.forEach((timelineObjects, guid) =>
				{
					let lastFadeinEffector: EffectorObjectFade | null = null;
					let lastFadeInEffectorEndTime = 0;

					for (const timelineObject of timelineObjects)
					{
						for (const channel of timelineObject.channels)
						{
							for (const fragment of channel.fragments)
							{
								switch (fragment.effectorType)
								{
									case TimelineEffectorTypes.ObjectFade:
										if (fragment.endTime >= lastFadeInEffectorEndTime)
										{
											lastFadeinEffector = fragment.effector;
											lastFadeInEffectorEndTime = fragment.endTime;
										}

										break;
								}
							}
						}
					}

					if (lastFadeinEffector && lastFadeinEffector.fadeType === FadeType.fadeOut)
					{
						let objectsToHide: THREE.Object3D[] = [];
						if (GroupsManager.getGroupByGuid(guid))
						{
							objectsToHide = GroupsManager.getGroupObjects(guid, destSceneGuid);
						}
						else
						{
							const timelineObjects = guidToTimelineObjects.get(guid)!;
							objectsToHide.push(sourceToClone.get(timelineObjects[0].associatedObject!)!);
						}

						for (const object of objectsToHide)
						{
							if (object.userData?.visibility)
							{
								object.userData.visibility[RenderModes.Device] = VisibilityModes.Hidden;
								object.userData.visibility[RenderModes.Standalone] = VisibilityModes.Hidden;
								ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Visibility);
							}
						}
					}
				});
			});
		}

		return clone;
	}

	static cloneNoneRecursive(source: THREE.Object3D): THREE.Object3D
	{
		let ret: THREE.Object3D | undefined = undefined;

		if (!(source instanceof THREE.Mesh))
		{
			ret = source.clone(false);
		}
		else
		{
			if (source instanceof THREE.SkinnedMesh)
			{
				ret = new THREE.SkinnedMesh(source.geometry, source.material);
			}
			else
			{
				ret = new THREE.Mesh(source.geometry, source.material);
			}

			THREE.Object3D.prototype.copy.call(ret, source, false);

			if (source.morphTargetInfluences !== undefined)
			{
				(ret as THREE.Mesh).morphTargetInfluences = source.morphTargetInfluences.slice();
			}

			if (source.morphTargetDictionary !== undefined)
			{
				(ret as THREE.Mesh).morphTargetDictionary = Object.assign({}, source.morphTargetDictionary);
			}

			if (source instanceof THREE.SkinnedMesh)
			{
				(ret as THREE.SkinnedMesh).skeleton = source.skeleton.clone();
			}
		}

		delete ret.userData.isCachedAssetObject;
		if (source.onAfterRender) ret.onAfterRender = source.onAfterRender;
		return ret;
	}

	/**
     * checks if the given object is a descendant of the given parent object
     * the function loops through the .parent of the object until the given parent is found (or no parent remains)
     *
     * @static
     * @param {THREE.Object3D} object
     * @param {THREE.Object3D} parent
     * @return {boolean} true if the given parent is one of the given object's .parent tree
     * @memberof SceneObjectUtils
     */
	static isDescendantOf(object: THREE.Object3D, parent: THREE.Object3D): boolean
	{
		if (object.userData.guid === parent.userData.guid) return false;

		let objectParent = object.parent;
		while (objectParent)
		{
			if (objectParent.userData.guid === parent.userData.guid)
			{
				return true;
			}

			objectParent = objectParent.parent;
		}

		return false;
	}

	static getSubNode(rootObject: THREE.Object3D, nodeIndex: number | undefined, primitiveIndex: number | undefined, assetId: string): THREE.Object3D | null
	{
		if (rootObject.userData.hideInHierarchy) return null;

		if (rootObject.userData.modelData &&
            rootObject.userData.modelData.nodeIndex === nodeIndex &&
            rootObject.userData.modelData.primitiveIndex === primitiveIndex &&
            rootObject.userData.assetId === assetId)
		{
			return rootObject;
		}

		for (const child of rootObject.children)
		{
			const childRet = SceneObjectUtils.getSubNode(child, nodeIndex, primitiveIndex, assetId);
			if (childRet !== null)
			{
				return childRet;
			}
		}

		return null;
	}

	/**
     * recursively searches the root object's hierarchy for an object that has the given guid
     *
     * @static
     * @param {THREE.Object3D} root
     * @param {string} guid
     * @return {*}  {(T | null)}
     * @memberof SceneObjectUtils
     */
	static findObjectByGuid<T extends THREE.Object3D = THREE.Object3D>(root: THREE.Object3D, guid: string): T | null
	{
		if (!guid || !root) return null;

		const stack: THREE.Object3D[] = [root];

		while (stack.length > 0) 
		{
			const current = stack.pop()!;
			
			if (current.userData.guid === guid) 
			{
				return current as T;
			}

			// Add all children to the stack
			for (let i = current.children.length - 1; i >= 0; i--) 
			{
				stack.push(current.children[i]);
			}
		}

		return null;
	}

	static getObjectTriangleCount(object: THREE.Object3D): number
	{
		if (object instanceof THREE.Mesh)
		{
			// simple geometry
			if (object.geometry.faces)
			{
				return object.geometry.faces.length;
			}
			// buffer geometry - indexed
			else if (object.geometry.index)
			{
				return object.geometry.index.count / 3;
			}
			// buffer geometry - not indexed
			else if (object.geometry.attributes.position)
			{
				return object.geometry.attributes.position.count / 3;
			}
		}

		return 0;
	}

	/**s
     * this function counts the triangles of the *entire* rootObject's hierarchy and the number of descendants of the given rootObject in the hierarchy. This function internally calls SceneObjectUtils.getObjectTriangleCount
     * - note: this function will return the triangles of any object gizmos too
     * @static
     * @param {THREE.Object3D} rootObject a scene object that will serve as the root for the calculation
     * @returns An array containing the stats: [Triangle Count, Descendant Count]. Triangle Count is the total number of triangles (faces) in the given rootObject hierarchy. this value can be 0 for objects with no geometries in their hiararchies at all
     * @memberof SceneObjectUtils
     */
	static calculateHierarchyStats(rootObject: THREE.Object3D): [number, number]
	{
		let triangleCount = 0;
		let descendantCount = 0;

		if (rootObject)
		{
			rootObject.traverse((c) =>
			{
				triangleCount += SceneObjectUtils.getObjectTriangleCount(c);
				if (c !== rootObject)
				{
					// triangleCount += Utils.getObjectTriangleCount (c, true); //true = avoid hidden
					if (!c.userData.hideInHierarchy)
					{
						descendantCount++;
					}
				}
			});
		}

		return [Math.ceil(triangleCount), descendantCount];
	}

	/**
     * this function counts the triangles of the *entire* rootObject's hierarchy. this function internally calls SceneObjectUtils.getObjectTriangleCount
     * - note: this function will return the triangles of any object gizmos too
     * @static
     * @param {THREE.Object3D} rootObject a scene object that will serve as the root for the calculation
     * @returns the total number of triangles (faces) in the given rootObject hierarchy. this value can be 0 for objects with no geometries in their hiararchies at all
     * @memberof SceneObjectUtils
     */
	static calculateHierarchyTriangleCount(rootObject: THREE.Object3D): number
	{
		let count = 0;

		if (rootObject)
		{
			rootObject.traverse((c) =>
			{
				count += SceneObjectUtils.getObjectTriangleCount(c);
			});
		}

		return Math.ceil(count);
	}

	static disposeObject(object: THREE.Object3D, disposeSharedData = true): void
	{
		if (!object)
		{
			console.info("disposeObject called for null object");
			return;
		}

		for (let i = object.children.length - 1; i >= 0; i--)
		{
			const child = object.children[i];
			SceneObjectUtils.disposeObject(child, disposeSharedData);
		}

		if ("onDispose" in object) (object as any).onDispose();
		if (object.userData?.animations) Engine.getInstance().deregisterObjectAnimationsForExport(object);

		MaterialAsset.disassociateMaterialAsset(object as THREE.Mesh);
		if (disposeSharedData)
		{
			if (object instanceof THREE.SkinnedMesh && object && object.skeleton.boneTexture)
			{
				object.skeleton.boneTexture.dispose();
				object.skeleton.boneTexture = null;
			}

			if (object instanceof THREE.Mesh)
			{
				if (object.geometry)
				{
					object.geometry.dispose();
					object.geometry = undefined;
				}

				if (object.material && !MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid))
				{
					if (!object.material.userData.persistent)
					{
						for (const materialProperty of Object.keys(object.material))
						{
							if (object.material[materialProperty] && object.material[materialProperty].isTexture)
							{
								//check if material uses a texture that was not initialized through the MaterialAsset texture loading interface
								if (!object.material[materialProperty].userData || !object.material[materialProperty].userData.assetId)
								{
									object.material[materialProperty].dispose();
									object.material[materialProperty] = null;
								}
							}
						}
						object.material.dispose();
						object.material = null;
					}
				}
			}
		}

		if (object.parent)
		{
			object.removeFromParent();
		}
	}

	/**
     * @static
     * @param {String} name
     * @returns an array of all objects found to have the given name
     * @memberof SceneObjectUtils
     */
	static findObjectsByName(name: string, root?: THREE.Object3D): THREE.Object3D[]
	{
		if (!root) root = Engine.getInstance().getSelectionRoot();
		const output: THREE.Object3D[] = [];

		root.traverse((object) =>
		{
			if (!object.userData?.hideInHierarchy && object.name === name) output.push(object);
		});

		return output;
	}

	static getObjectsWithTriggers(rootObject: THREE.Object3D, searchTerm?: string): THREE.Object3D[]
	{
		if (!Scenario.currentStep()) return [];
		if (!rootObject) rootObject = Engine.getInstance().getSelectionRoot(Scenario.currentStep()?.scene.userData.guid);
		const objects: THREE.Object3D[] = [];

		const areTriggersValid = (triggers: Trigger[] | undefined) =>
		{
			if (!triggers || triggers.length === 0) return false;

			for (const trigger of triggers)
			{
				if (!trigger.removedSyncedTrigger)
				{
					return true;
				}
			}

			return false;
		}

		rootObject.traverse((obj) =>
		{
			if (areTriggersValid(obj.userData.triggers))
			{
				if (!searchTerm)
				{
					objects.push(obj);
				}
				else
				{
					if (obj.name.toLowerCase().includes(searchTerm.toLowerCase()))
					{
						objects.push(obj);
					}
					else
					{
						for (const trigger of obj.userData.triggers as Trigger[])
						{
							if (trigger.type.toLowerCase().includes(searchTerm.toLowerCase()))
							{
								objects.push(obj);
								break;
							}
							if (trigger.type === ObjectTriggerTypes.ProjectLink && searchTerm.toLowerCase().startsWith('sc'))
							{
								objects.push(obj);
								break;
							}
						}
					}
				}
			}
		});

		return objects;
	}

	/**
	 * Returns the general type of the given object  
	 * *Note:* There is no distinction between skinned and non-skinned meshes  
	 * *Note:* Bones are considered as models since they are always a part of a skinned mesh, which is considered a Model
	 *
	 * @static
	 * @param {THREE.Object3D} object
	 * @return {*}  {ObjectClass}
	 * @memberof SceneObjectUtils
	 */
	static getObjectClass(object: THREE.Object3D): ObjectClasses
	{
		if (object instanceof MediaPanel)
		{
			const material = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
			if (material?.type === MaterialTypes.Video) return ObjectClasses.Video;
			if (material?.type !== MaterialTypes.DepthMask) return ObjectClasses.Image;
		}
		else if (object instanceof Text3D) return ObjectClasses.Text3D;
		else if (object instanceof ScopeLight) return ObjectClasses.Light;
		else if (object instanceof ScopeCamera) return ObjectClasses.Camera;
		else if (object instanceof AudioSource) return ObjectClasses.Audio;
		else if (object instanceof THREE.Mesh || object instanceof THREE.Bone || object instanceof THREE.SkinnedMesh) return ObjectClasses.Model;

		return ObjectClasses.Empty;
	}

	static applyColorChangeEffect(object)
	{
		const factor = object.userData.overrideColorFactor !== undefined ? object.userData.overrideColorFactor : 0.0;
		assert(object.userData.overrideColor !== undefined);

		if (object.onColorChangeEffect)
		{
			object.onColorChangeEffect(factor); //custom object types - 3d text
		}
		else
		{
			const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
			if (materialAsset)
			{
				let materialInstance = MaterialAsset.getTimelineMaterialInstance(object);
				if(!materialInstance)
				{
					if(object.material.userData.guid)
					{
						materialInstance = MaterialAsset.getOrCreateMaterialTimelineInstance(object, object.material);
					}
					else
					{
						materialInstance = MaterialAsset.getOrCreateMaterialTimelineInstance(object, materialAsset.internalMaterial as THREE.Material);

					}
				}
				materialInstance.onColorChangeEffect(factor);
			}
		}
	}

	static applyFadeInEffect(object)
	{
		const factor = object.userData.fadeInFactor !== undefined ? object.userData.fadeInFactor : 1.0;
		if (object.onFadeInEffect)
		{
			object.onFadeInEffect(factor); //custom object types - 3d text
		}
		else
		{
			const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
			if (materialAsset)
			{
				if (object.material.uniforms && object.material.uniforms["u_alphaMultiplier"])
				{
					if (!object.material.userData.isClonedInstance)
					{
						const clonedInstance = object.material.clone();
						clonedInstance.userData.isClonedInstance = true;
						object.material = clonedInstance;
					}
					object.material.uniforms["u_alphaMultiplier"].value = factor;
				}
				else
				{
					let materialInstance = MaterialAsset.getTimelineMaterialInstance(object);
					if(!materialInstance)
					{
						if(object.material.userData.guid)
						{
							materialInstance = MaterialAsset.getOrCreateMaterialTimelineInstance(object, object.material);
						}
						else
						{
							materialInstance = MaterialAsset.getOrCreateMaterialTimelineInstance(object, materialAsset.internalMaterial as THREE.Material);

						}
					}
					materialInstance.onFadeInEffect(factor);
				}
			}
		}
	}

	/**
	 * Calculate the center of the bounding box of the given objects
	 * Cursor AI wrote this
	 * @static
	 * @param {THREE.Object3D[]} objects
	 * @returns {THREE.Vector3} the center of the bounding box
	 * @memberof Utils
	 */
	static calculateBoundsCenter(objects: THREE.Object3D[]): THREE.Vector3 
	{
		// Create an empty bounding box
		const bounds = new THREE.Box3();
		
		// Expand the bounding box to include all objects
		for (const object of objects) 
		{
			// Method 1: If the object has geometry with a bounding box
			const geometry = (object as THREE.Mesh).geometry;
			const isMesh = (object as THREE.Mesh).isMesh;
			if (isMesh && geometry && !geometry.boundingBox) 
			{
				geometry.computeBoundingBox();
			}
			
			if (isMesh && geometry && geometry.boundingBox && !geometry.boundingBox.isEmpty()) 
			{
				// Create a temporary box to avoid modifying the original
				const tempBox = new THREE.Box3().copy(geometry.boundingBox);
				object.updateMatrixWorld();
				tempBox.applyMatrix4(object.matrixWorld);
				bounds.expandByPoint(tempBox.min);
				bounds.expandByPoint(tempBox.max);
			}
			else 
			{
				// Method 2: Use a utility function that handles objects without geometry
				const tempBox = new THREE.Box3();
				SceneObjectUtils.expandBoundsByObjectVisibleOnly(tempBox, object);
				
				if (!tempBox.isEmpty()) 
				{
					bounds.expandByPoint(tempBox.min);
					bounds.expandByPoint(tempBox.max);
				}
				else 
				{
					// Method 3: Fallback to just using the object's world position
					const worldPosition = new THREE.Vector3();
					object.getWorldPosition(worldPosition);
					bounds.expandByPoint(worldPosition);
				}
			}
		}
		
		// Calculate the center of the bounding box
		const center = new THREE.Vector3();
		bounds.getCenter(center);
		
		return center;
	}

	static getVisibleMeshes(): THREE.Object3D[] 
	{
		const visibleMeshes: THREE.Object3D[] = [];
		const scene = Engine.getInstance().getCurrentScene();
		if (scene)
		{
			scene.traverse((object) =>
			{
				if (object.userData.hideInHierarchy) return;
				if (!(object instanceof THREE.Mesh)) return;
				if (object.userData.visibility && VisibilityManager.getObjectVisualVisibilityMode(object) === VisibilityModes.Hidden) return;
				visibleMeshes.push(object);
			});
		}

		return visibleMeshes;
	}

	/**
	 * modified version of threejs box3 expandByObject
	 * only expands if the userData.hideInHierarchy is false
	 *
	 * source: https://github.com/mrdoob/three.js/blob/6e897f9a42d615403dfa812b45663149f2d2db3e/src/math/Box3.js#L193
	 *
	 * @param {THREE.Box3} box
	 * @param {THREE.Object3D} object
	 * @param {boolean} [precise = false]
	 * @returns
	 * @memberof Utils
	 */
	static expandBoundsByObjectVisibleOnly(box, object, precise = false)
	{
		const _box = new THREE.Box3();
		const _vector = new THREE.Vector3();

		object.updateWorldMatrix(false, false);

		const geometry = object.geometry;
		if (geometry && !object.isSprite)
		{
			if (precise && geometry.attributes != undefined && geometry.attributes.position !== undefined)
			{
				const position = geometry.attributes.position;
				for (let i = 0, l = position.count; i < l; i++)
				{
					_vector.fromBufferAttribute(position, i).applyMatrix4(object.matrixWorld);
					box.expandByPoint(_vector);
				}
			}
			else
			{
				if (geometry.boundingBox === null)
				{
					geometry.computeBoundingBox();
				}

				_box.copy(geometry.boundingBox);
				_box.applyMatrix4(object.matrixWorld);

				box.union(_box);
			}
		}

		const children = object.children;
		for (let i = 0, l = children.length; i < l; i++)
		{
			if (children[i].userData.hideInHierarchy)
			{
				continue;
			}

			SceneObjectUtils.expandBoundsByObjectVisibleOnly(box, children[i], precise);
		}

		return box;
	}
}
